import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Table from '../../../components/organisms/Table';
import TableCell from '../../../components/widgets/TableCell';
import TableCellCheckbox from '../../../components/widgets/TableCellCheckbox';
import { getPostbackMacroNetworkName } from '../../../selectors/partnerIntegration';
import {
    ATTRIBUTION_TAB_IDS,
    PLATFORM_TEMPLATE_DISALLOWED_EVENT_TYPES,
    POSTBACK_EVENT_TYPE_OPTIONS,
    POSTBACK_PLATFORM_OPTION,
    TRANSLATE_PREFIX,
} from '../../consts';
import { useFormDataUpdater } from '../../formUpdaters';
import { applyPostbackEventsPlatformsNext, getDeselectIssues } from '../../postbackEventsPlatformsUpdates';
import useConfirmDialog from '../../useConfirmDialog';
import sharedTableCss from '../formTable.css';
import css from './PostbackEventsPlatformsTable.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_OVERALL_SETUP_TAB`;
const CAMPAIGN_TYPES_PREFIX = `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORM_OPTIONS`;

const PostbackEventsPlatformsTable = ({ translate, readOnly = false }) => {
    const { values } = useFormikContext();
    const postbackMacroNetworkName = useSelector(getPostbackMacroNetworkName);
    const updateFormData = useFormDataUpdater();
    const tabData = values.formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP] || {};
    const eventsAndPlatforms = tabData.postback_events_and_platforms || [];
    const platformTemplate = values.formData?.[ATTRIBUTION_TAB_IDS.GENERAL]?.platform_template;
    const disallowedEventTypes = new Set(PLATFORM_TEMPLATE_DISALLOWED_EVENT_TYPES[platformTemplate] || []);

    const { confirm, dialog } = useConfirmDialog();

    const checkedMap = {};
    eventsAndPlatforms.forEach(entry => {
        checkedMap[entry.event_type] = new Set(entry.platforms || []);
    });

    const isChecked = (eventType, platform) => checkedMap[eventType]?.has(platform) ?? false;

    const applyNext = next =>
        updateFormData(prevFormData => applyPostbackEventsPlatformsNext(prevFormData, next, postbackMacroNetworkName));

    const handleToggle = (eventType, platform) => {
        const currentSet = new Set(checkedMap[eventType] || []);
        if (currentSet.has(platform)) {
            currentSet.delete(platform);
        } else {
            currentSet.add(platform);
        }
        const next = POSTBACK_EVENT_TYPE_OPTIONS.map(et => ({
            event_type: et,
            platforms: [...(et === eventType ? currentSet : checkedMap[et] || new Set())],
        })).filter(item => item.platforms.length > 0);

        const templatesFormData = values.formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES];
        const issues = getDeselectIssues({
            tabData,
            eventsAndPlatforms,
            next,
            templatesFormData,
            toggledEventType: eventType,
        });

        if (issues.length === 1) {
            const key = issues[0];
            confirm({
                titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.${key}_TITLE`,
                textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.${key}_TEXT`,
                onConfirm: () => applyNext(next),
            });
            return;
        }
        if (issues.length > 1) {
            confirm({
                titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DESELECT_MULTIPLE_TITLE`,
                body: (
                    <>
                        <Translate id={`${TAB_PREFIX}.CONFIRM_DIALOG.DESELECT_MULTIPLE_INTRO`} />
                        <ul>
                            {issues.map(key => (
                                <li key={key}>
                                    <Translate id={`${TAB_PREFIX}.CONFIRM_DIALOG.${key}_BULLET`} />
                                </li>
                            ))}
                        </ul>
                        <Translate id={`${TAB_PREFIX}.CONFIRM_DIALOG.DESELECT_MULTIPLE_OUTRO`} />
                    </>
                ),
                onConfirm: () => applyNext(next),
            });
            return;
        }
        applyNext(next);
    };

    const tableMetadata = [
        {
            name: 'event_type_label',
            displayName: `${TAB_PREFIX}.EVENT_TYPE_COLUMN`,
            cellComponent: TableCell,
            cellProps: { className: css.eventTypeCell },
        },
        ...POSTBACK_PLATFORM_OPTION.map(platform => ({
            key: platform,
            name: platform,
            displayName: `${CAMPAIGN_TYPES_PREFIX}.${platform.toUpperCase()}`,
            headerAlign: 'center',
            cellComponent: TableCellCheckbox,
            cellProps: {
                tdClassName: css.platformCell,
                onChange: (_, eventType) => handleToggle(eventType, platform),
            },
        })),
    ];

    const tableData = POSTBACK_EVENT_TYPE_OPTIONS.map(eventType => {
        const row = {
            key: eventType,
            event_type: eventType,
            event_type_label: translate(`${TAB_PREFIX}.EVENT_TYPES.${eventType.toUpperCase()}`),
            disabled: readOnly || disallowedEventTypes.has(eventType),
        };
        POSTBACK_PLATFORM_OPTION.forEach(platform => {
            row[platform] = isChecked(eventType, platform);
        });
        return row;
    });

    return (
        <>
            <Table
                metadata={tableMetadata}
                data={tableData}
                zebraTable={false}
                rowHoverEnabled={false}
                showInnerBorder={false}
                tableClass={sharedTableCss.table}
                theadClass={sharedTableCss.thead}
                thClass={sharedTableCss.th}
                trClass={sharedTableCss.row}
            />
            {dialog}
        </>
    );
};

PostbackEventsPlatformsTable.propTypes = {
    translate: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

export default withLocalize(PostbackEventsPlatformsTable);
